import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Timer, Tv } from 'lucide-react';
import BloombergPlayer from './BloombergPlayer';
import { cn } from '@/lib/utils';

interface TradingDayStatsProps {
  className?: string;
}

const TradingDayStats: React.FC<TradingDayStatsProps> = ({ className }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [marketStatus, setMarketStatus] = useState<'open' | 'closed' | 'pre' | 'after'>('closed');
  const [timeUntilClose, setTimeUntilClose] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now);
      
      // Market hours: 9:30 AM - 4:00 PM Eastern
      const eastern = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const hours = eastern.getHours();
      const minutes = eastern.getMinutes();
      const currentMinutes = hours * 60 + minutes;
      
      const marketOpen = 9 * 60 + 30; // 9:30 AM
      const marketClose = 16 * 60; // 4:00 PM
      const preMarketStart = 4 * 60; // 4:00 AM
      const afterMarketEnd = 20 * 60; // 8:00 PM
      
      const dayOfWeek = eastern.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      if (isWeekend) {
        setMarketStatus('closed');
        setTimeUntilClose('Weekend');
      } else if (currentMinutes >= marketOpen && currentMinutes < marketClose) {
        setMarketStatus('open');
        const minutesRemaining = marketClose - currentMinutes;
        const hoursRemaining = Math.floor(minutesRemaining / 60);
        const minsRemaining = minutesRemaining % 60;
        setTimeUntilClose(`${hoursRemaining}h ${minsRemaining}m`);
      } else if (currentMinutes >= preMarketStart && currentMinutes < marketOpen) {
        setMarketStatus('pre');
        const minutesUntilOpen = marketOpen - currentMinutes;
        const hoursRemaining = Math.floor(minutesUntilOpen / 60);
        const minsRemaining = minutesUntilOpen % 60;
        setTimeUntilClose(`Opens in ${hoursRemaining}h ${minsRemaining}m`);
      } else if (currentMinutes >= marketClose && currentMinutes < afterMarketEnd) {
        setMarketStatus('after');
        setTimeUntilClose('After Hours');
      } else {
        setMarketStatus('closed');
        setTimeUntilClose('Closed');
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (date: Date, timeZone: string) => {
    return date.toLocaleTimeString('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getMarketStatusColor = () => {
    switch (marketStatus) {
      case 'open': return 'text-green-400';
      case 'pre': return 'text-yellow-400';
      case 'after': return 'text-orange-400';
      default: return 'text-red-400';
    }
  };

  const getMarketStatusText = () => {
    switch (marketStatus) {
      case 'open': return 'MARKET OPEN';
      case 'pre': return 'PRE-MARKET';
      case 'after': return 'AFTER HOURS';
      default: return 'MARKET CLOSED';
    }
  };

  return (
    <div className={cn(
      'bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 rounded-lg overflow-hidden shadow-lg shadow-delphi-red/5',
      className
    )}>
      {/* Header with Bloomberg TV label and Live indicator */}
      <div className="px-3 py-2 border-b border-delphi-gray/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tv size={16} className="text-delphi-red" />
          <span className="font-semibold text-delphi-light text-sm">Bloomberg TV</span>
        </div>
        {/* Live Indicator - moved outside video */}
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          <span className="font-semibold text-red-500 text-xs uppercase tracking-wide">
            Live
          </span>
        </div>
      </div>

      {/* Bloomberg Player - larger size */}
      <div className="p-2">
        <BloombergPlayer className="shadow-lg shadow-black/40" hideHeader />
      </div>

      {/* Trading Day Stats - Centered with larger fonts */}
      <div className="px-3 pb-3 space-y-2">
        {/* Date */}
        <div className="text-center py-2 border-t border-delphi-gray/20">
          <div className="flex items-center justify-center gap-2">
            <Calendar size={16} className="text-delphi-red/80" />
            <span className="text-sm text-delphi-light/60 font-medium">Today's Date</span>
          </div>
          <span className="text-base font-semibold text-delphi-light">
            {formatDate(currentTime)}
          </span>
        </div>

        {/* Time Zones */}
        <div className="grid grid-cols-2 gap-2">
          {/* Eastern Time */}
          <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
            <div className="flex items-center justify-center gap-1.5">
              <Clock size={14} className="text-delphi-red/80" />
              <span className="text-xs text-delphi-light/60 font-medium uppercase tracking-wide">Eastern (ET)</span>
            </div>
            <span className="text-sm sm:text-base font-mono font-bold text-delphi-light">
              {formatTime(currentTime, 'America/New_York')}
            </span>
          </div>

          {/* Pacific Time */}
          <div className="text-center bg-delphi-gray/10 rounded-lg p-2">
            <div className="flex items-center justify-center gap-1.5">
              <Clock size={14} className="text-delphi-red/80" />
              <span className="text-xs text-delphi-light/60 font-medium uppercase tracking-wide">Pacific (PT)</span>
            </div>
            <span className="text-sm sm:text-base font-mono font-bold text-delphi-light">
              {formatTime(currentTime, 'America/Los_Angeles')}
            </span>
          </div>
        </div>

        {/* Market Status & Time Until Close - Clearer labels */}
        <div className="text-center pt-2 border-t border-delphi-gray/20">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Timer size={18} className={getMarketStatusColor()} />
            <span className={cn('text-sm font-bold uppercase tracking-wide', getMarketStatusColor())}>
              {marketStatus === 'open' ? 'Time Until Market Close' : 
               marketStatus === 'pre' ? 'Time Until Market Open' :
               marketStatus === 'after' ? 'After Hours Trading' : 'Market Status'}
            </span>
          </div>
          <span className={cn('text-xl font-bold', getMarketStatusColor())}>
            {timeUntilClose}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TradingDayStats;
